import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { NavBar, NavActions } from "@/components/NavBar";
import { Footer } from "@/components/Footer";
import { toast } from "sonner";
import { format } from "date-fns";
import { ArrowLeft, CalendarIcon, Plus, X } from "lucide-react";

function CreateJob() {
  var navigate = useNavigate();
  var auth = useAuth();
  var user = auth.user;
  var token = auth.token;

  var [loading, setLoading] = React.useState(false);
  var [title, setTitle] = React.useState("");
  var [description, setDescription] = React.useState("");
  var [jobType, setJobType] = React.useState("photo");
  var [date, setDate] = React.useState(null);
  var [hourlyRate, setHourlyRate] = React.useState("");
  var [flatRate, setFlatRate] = React.useState("");
  var [address, setAddress] = React.useState("");
  var [city, setCity] = React.useState("");
  var [state, setState] = React.useState("");
  var [zipCode, setZipCode] = React.useState("");
  var [requiredSkills, setRequiredSkills] = React.useState([]);
  var [requiredGear, setRequiredGear] = React.useState([]);
  var [newSkill, setNewSkill] = React.useState("");
  var [newGear, setNewGear] = React.useState("");

  function addSkill() {
    if (newSkill.trim() && !requiredSkills.includes(newSkill.trim())) {
      setRequiredSkills(requiredSkills.concat([newSkill.trim()]));
      setNewSkill("");
    }
  }

  function removeSkill(skill) {
    setRequiredSkills(requiredSkills.filter(function(s) { return s !== skill; }));
  }

  function addGear() {
    if (newGear.trim() && !requiredGear.includes(newGear.trim())) {
      setRequiredGear(requiredGear.concat([newGear.trim()]));
      setNewGear("");
    }
  }

  function removeGear(gear) {
    setRequiredGear(requiredGear.filter(function(g) { return g !== gear; }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    if (!title || !description || !date || !city) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);

    try {
      var coordinates = [];
      try {
        var geoResponse = await fetch(API + "/geocode?address=" + encodeURIComponent(address + " " + city + " " + state + " " + zipCode), {
          headers: {
            Authorization: "Bearer " + token
          }
        });
        if (geoResponse.ok) {
          var geoData = await geoResponse.json();
          coordinates = [geoData.lng, geoData.lat];
        }
      } catch (e) {
        console.log("Geocoding failed");
      }

      var jobData = {
        title: title,
        description: description,
        job_type: jobType,
        date: date.toISOString(),
        location: {
          type: "Point",
          coordinates: coordinates,
          address: address,
          city: city,
          state: state,
          zip_code: zipCode
        },
        hourly_rate: hourlyRate ? parseFloat(hourlyRate) : null,
        flat_rate: flatRate ? parseFloat(flatRate) : null,
        required_skills: requiredSkills,
        required_gear: requiredGear
      };

      var headers = { "Content-Type": "application/json" };
      if (token) headers.Authorization = "Bearer " + token;

      var response = await fetch(API + "/jobs", {
        method: "POST",
        headers: headers,
        body: JSON.stringify(jobData)
      });

      if (response.ok) {
        var data = await response.json();
        toast.success("Job posted successfully!");
        navigate("/jobs/" + data.job_id);
      } else {
        var error = await response.json();
        toast.error(error.detail || "Failed to create job");
      }
    } catch (error) {
      console.error("Create job error:", error);
      toast.error("Failed to create job");
    } finally {
      setLoading(false);
    }
  }

  if (!user || user.role !== "studio") {
    // Check both old and new format
    var userRoles = user ? (user.roles || [user.role]) : [];
    if (!userRoles.includes("studio")) {
      return (
        <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
          <Card className="bg-white/[0.03] border-white/5 p-8 text-center max-w-md">
            <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
            <p className="text-white/60 mb-6">Only studios can create job postings.</p>
            <Link to="/dashboard">
              <Button className="bg-white text-black hover:bg-gray-200 rounded-sm">
                Back to Dashboard
              </Button>
            </Link>
          </Card>
        </div>
      );
    }
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <NavBar>
        <NavActions />
      </NavBar>

      <main className="flex-1 pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <Link to="/dashboard" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-8" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm uppercase tracking-wider">Back to Dashboard</span>
          </Link>

          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-8">
            Post a New Job
          </h1>

          <form onSubmit={handleSubmit} className="space-y-8">
            <Card className="bg-white/[0.03] border-white/5 p-8">
              <h2 className="text-xl font-semibold mb-6">Basic Information</h2>
              
              <div className="space-y-6">
                <div>
                  <Label className="text-sm text-white/60 uppercase tracking-wider">Job Title *</Label>
                  <Input
                    value={title}
                    onChange={function(e) { setTitle(e.target.value); }}
                    placeholder="e.g., Second Shooter for Wedding"
                    className="mt-2 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm"
                    required
                    data-testid="job-title-input"
                  />
                </div>

                <div>
                  <Label className="text-sm text-white/60 uppercase tracking-wider">Description *</Label>
                  <Textarea
                    value={description}
                    onChange={function(e) { setDescription(e.target.value); }}
                    placeholder="Describe the job..."
                    className="mt-2 bg-white/[0.05] border-white/10 focus:border-white/40 text-white min-h-[150px] rounded-sm"
                    required
                    data-testid="job-description-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm text-white/60 uppercase tracking-wider">Job Type *</Label>
                    <select 
                      value={jobType} 
                      onChange={function(e) { setJobType(e.target.value); }}
                      className="mt-2 w-full h-12 bg-white/[0.05] border border-white/10 rounded-sm text-white px-3"
                      data-testid="job-type-select"
                    >
                      <option value="photo">Photography</option>
                      <option value="video">Videography</option>
                      <option value="content">Content Creation</option>
                      <option value="all">All/Mixed</option>
                    </select>
                  </div>

                  <div>
                    <Label className="text-sm text-white/60 uppercase tracking-wider">Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="mt-2 w-full bg-white/[0.05] border-white/10 text-white h-12 rounded-sm justify-start font-normal"
                          data-testid="job-date-btn"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date ? format(date, "PPP") : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-white/[0.05] border-white/10">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          initialFocus
                          disabled={function(d) { return d < new Date(); }}
                          data-testid="job-date-calendar"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="bg-white/[0.03] border-white/5 p-8">
              <h2 className="text-xl font-semibold mb-6">Location</h2>
              
              <div className="space-y-6">
                <div>
                  <Label className="text-sm text-white/60 uppercase tracking-wider">Street Address</Label>
                  <Input
                    value={address}
                    onChange={function(e) { setAddress(e.target.value); }}
                    placeholder="123 Main St"
                    className="mt-2 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm"
                    data-testid="address-input"
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="col-span-2">
                    <Label className="text-sm text-white/60 uppercase tracking-wider">City *</Label>
                    <Input
                      value={city}
                      onChange={function(e) { setCity(e.target.value); }}
                      placeholder="Los Angeles"
                      className="mt-2 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm"
                      required
                      data-testid="city-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-white/60 uppercase tracking-wider">State</Label>
                    <Input
                      value={state}
                      onChange={function(e) { setState(e.target.value); }}
                      placeholder="CA"
                      className="mt-2 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm"
                      data-testid="state-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-white/60 uppercase tracking-wider">ZIP</Label>
                    <Input
                      value={zipCode}
                      onChange={function(e) { setZipCode(e.target.value); }}
                      placeholder="90001"
                      className="mt-2 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm"
                      data-testid="zip-input"
                    />
                  </div>
                </div>
              </div>
            </Card>

            <Card className="bg-white/[0.03] border-white/5 p-8">
              <h2 className="text-xl font-semibold mb-6">Compensation (Optional)</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm text-white/60 uppercase tracking-wider">Hourly Rate</Label>
                  <div className="relative mt-2">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30">$</span>
                    <Input
                      type="number"
                      value={hourlyRate}
                      onChange={function(e) { setHourlyRate(e.target.value); }}
                      placeholder="50"
                      className="bg-white/[0.05] border-white/10 focus:border-white/40 text-white pl-8 h-12 rounded-sm"
                      data-testid="hourly-rate-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-white/60 uppercase tracking-wider">Flat Rate</Label>
                  <div className="relative mt-2">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30">$</span>
                    <Input
                      type="number"
                      value={flatRate}
                      onChange={function(e) { setFlatRate(e.target.value); }}
                      placeholder="500"
                      className="bg-white/[0.05] border-white/10 focus:border-white/40 text-white pl-8 h-12 rounded-sm"
                      data-testid="flat-rate-input"
                    />
                  </div>
                </div>
              </div>
            </Card>

            <Card className="bg-white/[0.03] border-white/5 p-8">
              <h2 className="text-xl font-semibold mb-6">Requirements (Optional)</h2>
              
              <div className="space-y-6">
                <div>
                  <Label className="text-sm text-white/60 uppercase tracking-wider">Required Skills</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={newSkill}
                      onChange={function(e) { setNewSkill(e.target.value); }}
                      onKeyPress={function(e) { if (e.key === "Enter") { e.preventDefault(); addSkill(); } }}
                      placeholder="e.g., Wedding Photography"
                      className="bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm"
                      data-testid="skill-input"
                    />
                    <Button type="button" onClick={addSkill} className="bg-white/10 hover:bg-white/20 text-white h-12 px-4 rounded-sm" data-testid="add-skill-btn">
                      <Plus className="w-5 h-5" />
                    </Button>
                  </div>
                  {requiredSkills.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {requiredSkills.map(function(skill) {
                        return (
                          <span key={skill} className="inline-flex items-center gap-1 bg-white/10 text-white px-3 py-1 rounded-sm text-sm">
                            {skill}
                            <button type="button" onClick={function() { removeSkill(skill); }} className="hover:text-red-400">
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-sm text-white/60 uppercase tracking-wider">Required Gear</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={newGear}
                      onChange={function(e) { setNewGear(e.target.value); }}
                      onKeyPress={function(e) { if (e.key === "Enter") { e.preventDefault(); addGear(); } }}
                      placeholder="e.g., Full-frame camera"
                      className="bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm"
                      data-testid="gear-input"
                    />
                    <Button type="button" onClick={addGear} className="bg-white/10 hover:bg-white/20 text-white h-12 px-4 rounded-sm" data-testid="add-gear-btn">
                      <Plus className="w-5 h-5" />
                    </Button>
                  </div>
                  {requiredGear.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {requiredGear.map(function(gear) {
                        return (
                          <span key={gear} className="inline-flex items-center gap-1 bg-white/10 text-white px-3 py-1 rounded-sm text-sm">
                            {gear}
                            <button type="button" onClick={function() { removeGear(gear); }} className="hover:text-red-400">
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </Card>

            <div className="flex gap-4">
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-white text-black hover:bg-gray-200 rounded-sm font-bold py-4 uppercase tracking-wide"
                data-testid="create-job-submit-btn"
              >
                {loading ? "Creating..." : "Post Job"}
              </Button>
              <Link to="/dashboard">
                <Button type="button" variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-sm py-4 px-8">
                  Cancel
                </Button>
              </Link>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default CreateJob;
